import React, { useState, useEffect, FC, useCallback, useMemo } from 'react';
import { BatteryIcon } from '../../components/icons/icons';
import { 
  BoltIcon, 
  ChartBarIcon, 
  HeartIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { ArrowPathIcon } from '@heroicons/react/24/solid';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import PageContainer from '../../components/layout/PageContainer';
import { useTheme } from '../../context/ThemeContext';
import { GatewayIcon } from '../../components/icons/icons';
import { fetchBatteryData, fetchBatteryIntradayData, BatteryData, BatteryHistorical } from '../../services/backend/SrcfulPublicApi';
import { useGateways } from '../../context/GatewayContext';

const BatteryPage: FC = () => {
  const { theme } = useTheme();
  const { gateways } = useGateways();
  const isLightMode = theme === 'light';

  // State for battery data
  const [batteryData, setBatteryData] = useState<BatteryData | null>(null);
  const [dailyHistoricalData, setDailyHistoricalData] = useState<BatteryHistorical[]>([]);
  const [intradayData, setIntradayData] = useState<BatteryHistorical[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Selected day for intraday view
  const [selectedDay, setSelectedDay] = useState<Date>(new Date());
  

  // Get battery DERs from all gateways
  const batteryDers = useMemo(() => 
    gateways?.flatMap(gateway => 
      (gateway.ders || [])
        .filter(der => der.type.toLowerCase().includes('battery'))
        .map(der => ({
          ...der,
          gatewayName: gateway.name,
          gatewayId: gateway.id
        }))
    ) || [], [gateways]
  );

  // Get the first available battery DER
  const selectedBattery = batteryDers[0];

  // Fetch battery data
  const fetchAllBatteryData = useCallback(async () => {
    if (!selectedBattery?.sn) {
      setError('No battery DER found');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      // Fetch daily historical data (last 30 days)
      const endDate = new Date().toISOString();
      const startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      
      const data = await fetchBatteryData(selectedBattery.sn, startDate, endDate, "24h");
      setBatteryData(data);
      setDailyHistoricalData(data.historical);
      
      // Fetch intraday data for selected day
      const dayStart = new Date(selectedDay);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(selectedDay);
      dayEnd.setHours(23, 59, 59, 999);
      
      const intradayData = await fetchBatteryIntradayData(
        selectedBattery.sn, 
        dayStart.toISOString(), 
        dayEnd.toISOString(), 
        "15m"
      );
      setIntradayData(intradayData);
      
    } catch (err) {
      console.error('Error fetching battery data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch battery data');
    } finally {
      setIsLoading(false);
    }
  }, [selectedDay, selectedBattery?.sn]);

  // Initial data fetch
  useEffect(() => {
    fetchAllBatteryData();
  }, [fetchAllBatteryData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(fetchAllBatteryData, 30000);
    return () => clearInterval(interval);
  }, [fetchAllBatteryData]);
  
  const getBatteryColor = (level: number) => {
    if (level < 20) return 'text-red-500';
    if (level < 50) return 'text-yellow-500';
    return 'text-[#00FF84]';
  };
  
  // Custom battery level indicator
  const BatteryLevelIndicator = ({ level }: { level: number }) => {
    const color = getBatteryColor(level).replace('text-', 'bg-');
    
    return (
      <div className="flex items-center">
        <div className="relative w-20 h-9 border-2 rounded-[12px] border-gray-600 bg-[#1D1D1D]">
          <div 
            className={`absolute left-0 top-0 bottom-0 ${color} transition-all duration-1000 ease-in-out rounded-l-[10px]`} 
            style={{ width: `${level}%` }}
          />
          <div className="absolute w-2 h-5 bg-gray-600 -right-2 top-1/2 transform -translate-y-1/2 rounded-r-sm" />
          <div className="absolute inset-0 p-[4px] flex items-center justify-center">
            <div className="bg-white/20 w-full h-full rounded-[8px] flex items-center justify-center">
              <span className="text-black font-bold text-sm">{Math.round(level)}%</span>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;

    return (
      <div className="bg-[#1D1D1D] border border-[#292929] rounded-[8px] p-3 shadow-lg">
        <p className="text-white text-sm font-medium mb-1">{label}</p>
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex items-center gap-2 mt-1">
            <div 
              className="w-2 h-2 rounded-full" 
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-400 text-sm">{entry.name}:</span>
            <span className="text-white font-medium">
              {entry.dataKey === 'soc' ? `${entry.value.toFixed(1)}%` : `${entry.value.toFixed(2)} kW`}
            </span>
          </div>
        ))}
      </div>
    );
  };
  
  return (
    <PageContainer>
      <div className="mb-6 sm:mb-8 mt-1 sm:mt-1">
        <div className="flex items-center justify-between gap-2 mb-2 sm:mb-4">
          <div className="flex items-center gap-2">
            <BatteryIcon className="w-5 h-5 text-[#00FF84]" state="active" />
            <h1 className={`text-xl font-bold ${isLightMode ? 'text-gray-800' : 'text-white'}`}>Battery Data</h1>
            <div className={`px-3 py-1 ${isLightMode ? 'bg-amber-100 text-amber-600' : 'bg-amber-600/20 text-amber-400'} text-xs font-medium rounded-full`}>
              Demo
            </div>
          </div>
          <div className="relative group">
            <button 
              className="px-4 py-2 bg-[#00FF84] text-black text-sm font-medium rounded-full w-full sm:w-auto sm:min-w-[120px] whitespace-nowrap"
            >
              Automater
            </button>
            <div className="absolute right-0 top-full mt-3 w-72 p-4 bg-[#1D1D1D] border border-[#292929] text-xs text-gray-300 rounded-[8px] invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity z-[100]">
              Coming soon: Automatically buy low and sell high using stored energy for maximum cost efficiency.
              <div className="absolute bottom-full right-4 border-4 border-transparent border-b-[#1D1D1D]"></div>
            </div>
          </div>
        </div>
      </div>

      <div className="h-px bg-[#292929] w-full mb-3 -mt-3"></div>

      <div className="flex items-center gap-2 mb-4">
        <GatewayIcon state="active" className="w-5 h-5" />
        <h2 className="text-xl font-medium text-white">
          {selectedBattery ? `${selectedBattery.gatewayName} - Battery` : 'No Battery Found'}
        </h2>
        {batteryData?.latest.ts && (
          <span className="text-sm text-gray-400">
            (Last update: {new Date(batteryData.latest.ts).toLocaleString()})
          </span>
        )}
      </div>

      {/* Show message if no battery is found */}
      {!selectedBattery && (
        <div className="bg-yellow-900/20 border border-yellow-500/50 rounded-[8px] p-4 mb-4">
          <div className="flex items-center gap-2">
            <InformationCircleIcon className="w-5 h-5 text-yellow-400" />
            <span className="text-yellow-400 font-medium">No Battery Found</span>
          </div>
          <p className="text-yellow-300 text-sm mt-1">
            No battery DERs are currently available from your connected gateways. 
            Please ensure a battery is connected and registered with your gateway.
          </p>
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="bg-red-900/20 border border-red-500/50 rounded-[8px] p-4 mb-4">
          <div className="flex items-center gap-2">
            <InformationCircleIcon className="w-5 h-5 text-red-400" />
            <span className="text-red-400 font-medium">Error loading battery data</span>
          </div>
          <p className="text-red-300 text-sm mt-1">{error}</p>
          <button 
            onClick={fetchAllBatteryData}
            className="mt-2 px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      )}
      
      <div className="space-y-4 sm:space-y-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Current Status */}
            <div className="bg-[#1D1D1D] p-4 rounded-[8px] border border-[#292929]">
              <h2 className="text-base font-semibold text-white mb-4 flex items-center">
                <BoltIcon className="w-5 h-5 mr-2 text-[#00FF84]" />
                Current Status
              </h2>
              <div className="divide-y divide-[#292929]">
                <div className="py-3 flex justify-between items-center">
                  <span className="text-gray-400">Battery Level</span>
                  <BatteryLevelIndicator level={batteryData?.latest.soc || 0} />
                </div>
                <div className="py-2 flex justify-between">
                  <span className="text-gray-400">Status</span>
                  <span className={`${batteryData?.latest.power && batteryData.latest.power > 0 ? 'text-[#00FF84]' : batteryData?.latest.power && batteryData.latest.power < 0 ? 'text-orange-500' : 'text-gray-400'}`}>
                    {batteryData?.latest.power ? (batteryData.latest.power > 0 ? 'Charging' : batteryData.latest.power < 0 ? 'Discharging' : 'Idle') : 'Unknown'}
                  </span>
                </div>
                <div className="py-2 flex justify-between">
                  <span className="text-gray-400">Current Power</span>
                  <span className="text-white">{batteryData?.latest.power ? Math.abs(batteryData.latest.power / 1000).toFixed(2) : '0.0'} kW</span>
                </div>
                <div className="py-2 flex justify-between">
                  <span className="text-gray-400">Current</span>
                  <span className="text-white">{batteryData?.latest.current?.toFixed(1) || '0.0'} A</span>
                </div>
                <div className="py-2 flex justify-between">
                  <span className="text-gray-400">Voltage</span>
                  <span className="text-white">{batteryData?.latest.voltage?.toFixed(1) || '0.0'} V</span>
                </div>
              </div>
            </div>
            
            {/* Battery Info */}
            <div className="bg-[#1D1D1D] p-4 rounded-[8px] border border-[#292929]">
              <h2 className="text-base font-semibold text-white mb-4 flex items-center">
                <ArrowPathIcon className="w-5 h-5 mr-2 text-[#00FF84]" />
                Battery Information
              </h2>
              <div className="divide-y divide-[#292929]">
                <div className="py-2 flex justify-between">
                  <span className="text-gray-400">Serial Number</span>
                  <span className="text-white text-xs font-mono">{selectedBattery?.sn?.slice(-8) || 'N/A'}</span>
                </div>
                <div className="py-2 flex justify-between">
                  <span className="text-gray-400">Last Updated</span>
                  <span className="text-white text-sm">
                    {batteryData?.latest.ts ? new Date(batteryData.latest.ts).toLocaleTimeString() : 'N/A'}
                  </span>
                </div>
                <div className="py-2 flex justify-between">
                  <span className="text-gray-400">Data Points</span>
                  <span className="text-white">{dailyHistoricalData.length} days</span>
                </div>
                <div className="py-2 flex justify-between">
                  <span className="text-gray-400">Connection</span>
                  <span className={`${batteryData ? 'text-[#00FF84]' : 'text-gray-400'}`}>
                    {batteryData ? 'Connected' : 'Disconnected'}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Battery Statistics */}
            <div className="bg-[#1D1D1D] p-4 rounded-[8px] border border-[#292929]">
              <h2 className="text-base font-semibold text-white mb-4 flex items-center">
                <HeartIcon className="w-5 h-5 mr-2 text-[#00FF84]" />
                Battery Statistics
              </h2>
              <div className="divide-y divide-[#292929]">
                <div className="py-2 flex justify-between">
                  <span className="text-gray-400">Average SoC</span>
                  <span className="text-white">
                    {dailyHistoricalData.length > 0 
                      ? `${(dailyHistoricalData.reduce((sum, item) => sum + item.soc, 0) / dailyHistoricalData.length).toFixed(1)}%`
                      : 'N/A'
                    }
                  </span>
                </div>
                <div className="py-2 flex justify-between">
                  <span className="text-gray-400">Max SoC (30d)</span>
                  <span className="text-white">
                    {dailyHistoricalData.length > 0 
                      ? `${Math.max(...dailyHistoricalData.map(item => item.soc)).toFixed(1)}%`
                      : 'N/A'
                    }
                  </span>
                </div>
                <div className="py-2 flex justify-between">
                  <span className="text-gray-400">Min SoC (30d)</span>
                  <span className="text-white">
                    {dailyHistoricalData.length > 0 
                      ? `${Math.min(...dailyHistoricalData.map(item => item.soc)).toFixed(1)}%`
                      : 'N/A'
                    }
                  </span>
                </div>
                <div className="py-2 flex justify-between">
                  <span className="text-gray-400">Average Power</span>
                  <span className="text-white">
                    {dailyHistoricalData.length > 0 
                      ? `${(Math.abs(dailyHistoricalData.reduce((sum, item) => sum + item.power, 0)) / dailyHistoricalData.length / 1000).toFixed(2)} kW`
                      : 'N/A'
                    }
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Daily State of Charge */}
          <div className="mt-6">
            <div className="bg-[#1D1D1D] p-4 rounded-[8px] border border-[#292929]">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-base font-semibold text-white flex items-center">
                  <ChartBarIcon className="w-5 h-5 mr-2 text-[#00FF84]" />
                  Daily State of Charge
                </h2>
                {isLoading && (
                  <div className="w-4 h-4 border border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                )}
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={dailyHistoricalData.map(item => ({
                      time: new Date(item.ts).toLocaleDateString(),
                      soc: item.soc,
                      ts: item.ts
                    }))}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#292929" />
                    <XAxis 
                      dataKey="time" 
                      stroke="#666" 
                      tick={{ fill: '#999' }}
                    />
                    <YAxis 
                      stroke="#666" 
                      tick={{ fill: '#999' }}
                      label={{ value: 'SoC (%)', position: 'insideLeft', fill: '#999' }}
                      domain={[0, 100]}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="soc" 
                      stroke="#00FF84" 
                      strokeWidth={2}
                      dot={false}
                      name="State of Charge"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Intraday Power and SOC */}
          <div className="mt-6">
            <div className="bg-[#1D1D1D] p-4 rounded-[8px] border border-[#292929]">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-base font-semibold text-white flex items-center">
                  <BoltIcon className="w-5 h-5 mr-2 text-[#00FF84]" />
                  Intraday Power & State of Charge
                </h2>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={selectedDay.toISOString().split('T')[0]}
                    onChange={(e) => setSelectedDay(new Date(e.target.value))}
                    className="px-3 py-1 bg-[#292929] text-white text-sm rounded"
                  />
                  {isLoading && (
                    <div className="w-4 h-4 border border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  )}
                </div>
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={intradayData.map(item => ({
                      time: new Date(item.ts).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
                      power: item.power / 1000, // Convert to kW
                      soc: item.soc,
                      ts: item.ts
                    }))}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#292929" />
                    <XAxis 
                      dataKey="time" 
                      stroke="#666" 
                      tick={{ fill: '#999' }}
                    />
                    <YAxis 
                      yAxisId="power"
                      stroke="#666" 
                      tick={{ fill: '#999' }}
                      label={{ value: 'Power (kW)', position: 'insideLeft', fill: '#999' }}
                    />
                    <YAxis 
                      yAxisId="soc"
                      orientation="right"
                      stroke="#666" 
                      tick={{ fill: '#999' }}
                      label={{ value: 'SoC (%)', position: 'insideRight', fill: '#999' }}
                      domain={[0, 100]}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line 
                      yAxisId="power"
                      type="monotone" 
                      dataKey="power" 
                      stroke="#FF6B6B" 
                      strokeWidth={2}
                      dot={false}
                      name="Power (kW)"
                    />
                    <Line 
                      yAxisId="soc"
                      type="monotone" 
                      dataKey="soc" 
                      stroke="#00FF84" 
                      strokeWidth={2}
                      dot={false}
                      name="State of Charge (%)"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

      </div>
      
      <div className="mt-8 text-sm text-gray-400 w-full text-center">
        Live battery data from Srcful network • Updates every 30 seconds
      </div>
    </PageContainer>
  );
};

export default BatteryPage; 